import { useMediaQuery, useTheme } from "@mui/material"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback } from "react"

const NAV_DRAWER_OPEN_KEY = ["nav-drawer-open"] as const

/**
 * Shared open/collapsed state for the app's left nav drawer. Held in the
 * react-query cache (in-memory, NOT persisted) so the drawer and any other
 * component — e.g. layouts that reflow when the nav collapses — read the same
 * live value without a bespoke provider. Defaults to open on md+ viewports and
 * resets per session, matching the drawer's original local-state behavior.
 */
export const useNavDrawerOpen = (): {
  isOpen: boolean
  setIsOpen: (open: boolean) => void
} => {
  const theme = useTheme()
  const md = useMediaQuery(theme.breakpoints.up("md"))
  const queryClient = useQueryClient()

  // Cache-only entry: queryFn never runs (enabled: false); `initialData` seeds
  // the default and `setQueryData` is the only writer.
  const { data: isOpen } = useQuery({
    queryKey: NAV_DRAWER_OPEN_KEY,
    queryFn: () => true,
    enabled: false,
    initialData: md,
    staleTime: Infinity,
    gcTime: Infinity,
  })
  const setIsOpen = useCallback(
    (open: boolean) => {
      queryClient.setQueryData(NAV_DRAWER_OPEN_KEY, open)
    },
    [queryClient]
  )

  return { isOpen, setIsOpen }
}
